import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { RevealLines } from "./Reveal";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&w=2400&q=80";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end pt-28 pb-16 overflow-hidden spotlight-vehicle grain"
    >
      {/* Parallax background image */}
      <motion.div style={{ y, scale, opacity }} className="absolute inset-0 hero-mask">
        <img
          src={HERO_IMG}
          alt="Maklan Motors"
          className="w-full h-full object-cover object-center"
        />
        {/* Deeper cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050814]/70 via-[#050814]/55 to-[#050814]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050814]/80 via-transparent to-[#050814]/50" />
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at center, rgba(11,31,58,0) 0%, rgba(5,8,20,0.55) 70%, rgba(5,8,20,0.95) 100%)",
        }} />
      </motion.div>

      <div className="relative z-10 w-full container-editorial">
        {/* Parent brand hero-label */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.1 }}
          className="mb-10 flex items-center gap-4"
          data-testid="hero-parent-label"
        >
          <motion.span
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1], delay: 0.4 }}
            className="block h-px bg-[#C5A059] w-10 origin-left"
          />
          <span className="text-[10px] tracking-[0.5em] uppercase text-white/60">A House of</span>
          <span
            className="font-display uppercase text-[#C5A059] text-[1.35rem] md:text-[1.6rem] leading-none"
            style={{ letterSpacing: "0.42em" }}
          >
            Maklan
          </span>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-9">
            <RevealLines
              element="h1"
              className="h-editorial text-[clamp(3.2rem,10vw,10rem)]"
              lines={[
                "Bringing the",
                "<em>world</em> to you.",
              ]}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mt-10 max-w-xl text-white/70 text-lg leading-relaxed font-light"
              data-testid="hero-subtitle"
            >
              Maklan is a premium global sourcing and commerce company connecting
              Africa with the world's finest manufacturers. Motors is its
              automotive division — a pre-order sourcing house, not a dealership.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.3, delay: 1.6, ease: [0.19,1,0.22,1] }}
              className="mt-12 flex flex-wrap gap-4"
            >
              <Link to="/inventory" className="btn-editorial btn-editorial-gold group" data-testid="hero-cta-inventory">
                The Collection
                <ArrowRight className="transition-transform group-hover:translate-x-1" size={14} />
              </Link>
              <Link to="/about" className="btn-editorial" data-testid="hero-cta-about">
                About Maklan
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.4 }}
            className="md:col-span-3 md:pl-8 md:border-l md:border-white/10"
          >
            <div className="overline mb-3">Featured Import</div>
            <div className="font-display text-2xl leading-tight">Jetour T2 <em className="italic text-[#C5A059]">PHEV</em></div>
            <div className="text-white/50 text-sm mt-1">129 km EV range · 26.7 kWh</div>
            <Link to="/vehicle/jetour-t2-phev-2024" data-testid="hero-featured-link" className="mt-4 inline-flex items-center text-[13px] uppercase tracking-[0.14em] text-white/80 hover:text-[#C5A059] transition">
              View
              <ArrowRight className="ml-2" size={14} />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Bottom vertical index */}
      <div className="hidden md:flex absolute right-8 bottom-10 z-10 flex-col items-end gap-3 text-white/50">
        <span className="overline">Scroll</span>
        <div className="w-px h-16 bg-white/25 relative overflow-hidden">
          <span className="absolute inset-x-0 top-0 h-1/3 bg-[#C5A059] animate-[slide_2.4s_ease-in-out_infinite]"/>
        </div>
      </div>

      {/* Hairline gold transition into next section */}
      <div className="absolute inset-x-0 bottom-0 z-10">
        <div className="mx-auto max-w-[1320px] px-6 md:px-12">
          <div className="hairline-gold" />
        </div>
      </div>

      <style>{`@keyframes slide { 0%,100%{transform:translateY(-100%)} 50%{transform:translateY(300%)} }`}</style>
    </section>
  );
}
